import { useEffect, useRef } from 'react';
import {
  ProgressIndicator,
  Text,
  IconButton,
  Spinner,
  SpinnerSize,
  DefaultButton,
} from '@fluentui/react';
import { useFileBrowserStore } from '@/hooks';

export function UploadProgress() {
  const { uploads, clearCompletedUploads, cancelUpload, cancelAllUploads } = useFileBrowserStore();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isUploading = uploads.some((u) => u.status === 'uploading' || u.status === 'pending');
  const allDone =
    uploads.length > 0 &&
    uploads.every(
      (u) => u.status === 'complete' || u.status === 'error' || u.status === 'cancelled',
    );

  // Auto-close after 5 seconds when all uploads are done.
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (allDone && uploads.length > 0) {
      timerRef.current = setTimeout(() => {
        clearCompletedUploads();
      }, 5000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [allDone, uploads.length, clearCompletedUploads]);

  // Handle close button click
  const handleClose = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    clearCompletedUploads();
  };

  // Don't render if no uploads
  if (uploads.length === 0) {
    return null;
  }

  // Format path for display
  const formatPath = (path?: string) => {
    if (!path || path === '/') return 'Root';
    const cleanPath = path.replace(/\/$/, '');
    return cleanPath || 'Root';
  };

  // Batch summary: "Saved 8, 1 failed" once everything settles.
  const counts = {
    saved: uploads.filter((u) => u.status === 'complete').length,
    failed: uploads.filter((u) => u.status === 'error').length,
    cancelled: uploads.filter((u) => u.status === 'cancelled').length,
  };
  const summary = [
    counts.saved > 0 ? `Saved ${counts.saved}` : null,
    counts.failed > 0 ? `${counts.failed} failed` : null,
    counts.cancelled > 0 ? `${counts.cancelled} cancelled` : null,
  ]
    .filter(Boolean)
    .join(', ');

  const title = isUploading
    ? `Uploading ${counts.saved + counts.failed} of ${uploads.length}`
    : summary || 'Upload Complete';

  return (
    <div className="upload-progress">
      <div className="upload-header">
        <Text className="upload-title">{title}</Text>
        {isUploading && (
          <DefaultButton
            text="Cancel all"
            onClick={cancelAllUploads}
            styles={{ root: { height: 24, minWidth: 0, padding: '0 8px', fontSize: 11 } }}
          />
        )}
        <IconButton
          iconProps={{ iconName: 'Cancel' }}
          title="Close"
          onClick={handleClose}
          styles={{ root: { height: 24, width: 24 }, icon: { fontSize: 12 } }}
        />
      </div>

      <div className="upload-list">
        {uploads.map((upload) => (
          <div key={upload.id} className={`upload-item ${upload.status}`}>
            <div className="upload-file-info">
              <Text className="upload-filename" title={upload.fileName}>
                {upload.fileName}
              </Text>
              {upload.targetPath && (
                <Text className="upload-path">→ {formatPath(upload.targetPath)}</Text>
              )}
              {upload.status === 'error' && upload.error && (
                <Text className="upload-error" title={upload.error}>
                  {upload.error}
                </Text>
              )}
              {upload.status === 'cancelled' && (
                <Text className="upload-duplicate-note">Cancelled</Text>
              )}
            </div>
            <div className="upload-status-indicator">
              {upload.status === 'pending' && (
                <Spinner size={SpinnerSize.small} className="upload-spinner" />
              )}
              {upload.status === 'uploading' && (
                <Spinner size={SpinnerSize.small} className="upload-spinner" />
              )}
              {upload.status === 'complete' && <Text className="upload-status success">✓</Text>}
              {upload.status === 'error' && <Text className="upload-status error">✗</Text>}
              {upload.status === 'cancelled' && <Text className="upload-status">–</Text>}
              {(upload.status === 'uploading' || upload.status === 'pending') && (
                <IconButton
                  iconProps={{ iconName: 'Cancel' }}
                  title="Cancel upload"
                  onClick={() => cancelUpload(upload.id)}
                  styles={{ root: { height: 20, width: 20 }, icon: { fontSize: 10 } }}
                />
              )}
            </div>

            {(upload.status === 'uploading' || upload.status === 'pending') && (
              <ProgressIndicator
                percentComplete={upload.progress / 100}
                className="upload-progress-bar"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
